import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Form, Input, Switch, Space, message } from 'antd';
import { SaveOutlined, EyeOutlined, CloseOutlined } from '@ant-design/icons';
import 'grapesjs/dist/css/grapes.min.css';
import ApiClient from '../helpers/ApiClient';

const GrapesJSBuilder = ({ visible, onClose, editingPage, onSave }) => {
  const editorRef = useRef(null);
  const [editor, setEditor] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    const slug = generateSlug(title);
    form.setFieldsValue({ slug });
  };

  useEffect(() => {
    if (visible && !editor) {
      initEditor();
    }
  }, [visible]);

  useEffect(() => {
    return () => {
      if (editor) {
        try {
          editor.destroy();
        } catch (e) {
          console.log('Editor cleanup error:', e);
        }
        setEditor(null);
      }
    };
  }, [editor]);

  useEffect(() => {
    if (editingPage) {
      form.setFieldsValue({
        title: editingPage.title || '',
        slug: editingPage.slug || '',
        isActive: editingPage.isActive || false,
        metaTitle: editingPage.metaTitle || '',
        metaDescription: editingPage.metaDescription || '',
        keywords: editingPage.keywords || ''
      });
    }
  }, [editingPage, form]);

  useEffect(() => {
    if (editor && editingPage) {
      if (editingPage.htmlContent) {
        editor.setComponents(editingPage.htmlContent);
      }
      if (editingPage.cssContent) {
        editor.setStyle(editingPage.cssContent);
      }
    }
  }, [editor, editingPage]);

  const initEditor = async () => {
    try {
      const grapesjs = await import('grapesjs');
      const editorInstance = grapesjs.default.init({
        container: editorRef.current,
        height: '100vh',
        width: 'auto',
        storageManager: false,
        canvas: {
          styles: ['https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/css/bootstrap.min.css'],
          scripts: [
            'https://code.jquery.com/jquery-3.3.1.slim.min.js',
            'https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/js/bootstrap.min.js'
          ]
        },
        blockManager: {
          appendTo: '.blocks-container',
          blocks: [
            {
              id: 'hero-section',
              label: 'Hero Section',
              category: 'Travel',
              content: `
                <section style="background: linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'); background-size: cover; background-position: center; padding: 100px 0; color: white; text-align: center;">
                  <div class="container">
                    <h1 style="font-size: 3rem; margin-bottom: 20px;">Explore Amazing Destinations</h1>
                    <p style="font-size: 1.2rem; margin-bottom: 30px;">Discover the world's most beautiful places</p>
                    <button class="btn btn-primary btn-lg">Book Now</button>
                  </div>
                </section>
              `,
              media: '<i class="fa fa-image"></i>'
            },
            {
              id: 'video-section',
              label: 'Video Section',
              category: 'Media',
              content: `
                <section style="padding: 50px 0; text-align: center;">
                  <div class="container">
                    <h2 style="margin-bottom: 30px;">Watch Our Video</h2>
                    <div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%;">
                      <iframe src="https://www.youtube.com/embed/dQw4w9WgXcQ" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;" allowfullscreen></iframe>
                    </div>
                  </div>
                </section>
              `,
              media: '<i class="fa fa-play-circle"></i>'
            },
            {
              id: 'text-section',
              label: 'Text Section',
              category: 'Basic',
              content: '<div class="container" style="padding: 50px 0;"><h2>Your Title Here</h2><p>Your content goes here...</p></div>',
              media: '<i class="fa fa-text-width"></i>'
            },
            {
              id: 'image-section',
              label: 'Image Section',
              category: 'Media',
              content: `
                <section style="padding: 50px 0; text-align: center;">
                  <div class="container">
                    <h2 style="margin-bottom: 30px;">Beautiful Destinations</h2>
                    <img src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Destination" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1);">
                    <p style="margin-top: 15px; font-style: italic; color: #666;">Caption for your image</p>
                  </div>
                </section>
              `,
              media: '<i class="fa fa-picture-o"></i>'
            },
            {
              id: 'two-columns',
              label: '2 Columns',
              category: 'Layout',
              content: `
                <div class="container" style="padding: 50px 0;">
                  <div class="row">
                    <div class="col-md-6">
                      <h3>Column 1</h3>
                      <p>Content for first column</p>
                    </div>
                    <div class="col-md-6">
                      <h3>Column 2</h3>
                      <p>Content for second column</p>
                    </div>
                  </div>
                </div>
              `,
              media: '<i class="fa fa-columns"></i>'
            },
            {
              id: 'three-columns',
              label: '3 Columns',
              category: 'Layout',
              content: `
                <div class="container" style="padding: 50px 0;">
                  <div class="row">
                    <div class="col-md-4">
                      <h3>Column 1</h3>
                      <p>Content for first column</p>
                    </div>
                    <div class="col-md-4">
                      <h3>Column 2</h3>
                      <p>Content for second column</p>
                    </div>
                    <div class="col-md-4">
                      <h3>Column 3</h3>
                      <p>Content for third column</p>
                    </div>
                  </div>
                </div>
              `,
              media: '<i class="fa fa-th-large"></i>'
            },
            {
              id: 'four-columns',
              label: '4 Columns',
              category: 'Layout',
              content: `
                <div class="container" style="padding: 50px 0;">
                  <div class="row">
                    <div class="col-md-3">
                      <h4>Column 1</h4>
                      <p>Content here</p>
                    </div>
                    <div class="col-md-3">
                      <h4>Column 2</h4>
                      <p>Content here</p>
                    </div>
                    <div class="col-md-3">
                      <h4>Column 3</h4>
                      <p>Content here</p>
                    </div>
                    <div class="col-md-3">
                      <h4>Column 4</h4>
                      <p>Content here</p>
                    </div>
                  </div>
                </div>
              `,
              media: '<i class="fa fa-th"></i>'
            },
            {
              id: 'two-row-grid',
              label: '2 Rows Grid',
              category: 'Layout',
              content: `
                <div class="container" style="padding: 50px 0;">
                  <div class="row mb-4">
                    <div class="col-md-6">
                      <h3>Row 1 - Column 1</h3>
                      <p>Content for first row, first column</p>
                    </div>
                    <div class="col-md-6">
                      <h3>Row 1 - Column 2</h3>
                      <p>Content for first row, second column</p>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6">
                      <h3>Row 2 - Column 1</h3>
                      <p>Content for second row, first column</p>
                    </div>
                    <div class="col-md-6">
                      <h3>Row 2 - Column 2</h3>
                      <p>Content for second row, second column</p>
                    </div>
                  </div>
                </div>
              `,
              media: '<i class="fa fa-table"></i>'
            },
            {
              id: 'three-row-grid',
              label: '3 Rows Grid',
              category: 'Layout',
              content: `
                <div class="container" style="padding: 50px 0;">
                  <div class="row mb-3">
                    <div class="col-md-4"><h4>R1-C1</h4><p>Content</p></div>
                    <div class="col-md-4"><h4>R1-C2</h4><p>Content</p></div>
                    <div class="col-md-4"><h4>R1-C3</h4><p>Content</p></div>
                  </div>
                  <div class="row mb-3">
                    <div class="col-md-4"><h4>R2-C1</h4><p>Content</p></div>
                    <div class="col-md-4"><h4>R2-C2</h4><p>Content</p></div>
                    <div class="col-md-4"><h4>R2-C3</h4><p>Content</p></div>
                  </div>
                  <div class="row">
                    <div class="col-md-4"><h4>R3-C1</h4><p>Content</p></div>
                    <div class="col-md-4"><h4>R3-C2</h4><p>Content</p></div>
                    <div class="col-md-4"><h4>R3-C3</h4><p>Content</p></div>
                  </div>
                </div>
              `,
              media: '<i class="fa fa-th-list"></i>'
            },
            {
              id: 'gallery-grid',
              label: 'Photo Gallery',
              category: 'Media',
              content: `
                <section style="padding: 50px 0;">
                  <div class="container">
                    <h2 class="text-center mb-5">Photo Gallery</h2>
                    <div class="row">
                      <div class="col-md-4 mb-4">
                        <img src="https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" class="img-fluid rounded" style="width: 100%; height: 250px; object-fit: cover;">
                      </div>
                      <div class="col-md-4 mb-4">
                        <img src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" class="img-fluid rounded" style="width: 100%; height: 250px; object-fit: cover;">
                      </div>
                      <div class="col-md-4 mb-4">
                        <img src="https://images.unsplash.com/photo-1539650116574-75c0c6d73f6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" class="img-fluid rounded" style="width: 100%; height: 250px; object-fit: cover;">
                      </div>
                      <div class="col-md-4 mb-4">
                        <img src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" class="img-fluid rounded" style="width: 100%; height: 250px; object-fit: cover;">
                      </div>
                      <div class="col-md-4 mb-4">
                        <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" class="img-fluid rounded" style="width: 100%; height: 250px; object-fit: cover;">
                      </div>
                      <div class="col-md-4 mb-4">
                        <img src="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" class="img-fluid rounded" style="width: 100%; height: 250px; object-fit: cover;">
                      </div>
                    </div>
                  </div>
                </section>
              `,
              media: '<i class="fa fa-th"></i>'
            },
            {
              id: 'booking-form',
              label: 'Booking Form',
              category: 'Travel',
              content: `
                <section style="padding: 50px 0; background: #f8f9fa;">
                  <div class="container">
                    <div class="row justify-content-center">
                      <div class="col-md-6">
                        <div class="card">
                          <div class="card-body">
                            <h3 class="card-title text-center mb-4">Book Your Trip</h3>
                            <form class="booking-form-submit">
                              <div class="form-group">
                                <label>Full Name</label>
                                <input type="text" class="form-control" id="enquiry-name" placeholder="Enter your name" required>
                              </div>
                              <div class="form-group">
                                <label>Email</label>
                                <input type="email" class="form-control" id="enquiry-email" placeholder="Enter your email" required>
                              </div>
                              <div class="form-group">
                                <label>Phone</label>
                                <input type="tel" class="form-control" id="enquiry-phone" placeholder="Enter your phone" required>
                              </div>
                              <button type="button" class="btn btn-primary btn-block" onclick="submitEnquiry(this)">Submit Enquiry</button>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              `,
              media: '<i class="fa fa-wpforms"></i>'
            },
            {
              id: 'content-paragraph',
              label: 'Content Section',
              category: 'Basic',
              content: `
                <section style="padding: 50px 0;">
                  <div class="container">
                    <div class="row justify-content-center">
                      <div class="col-md-8">
                        <h2 style="text-align: center; margin-bottom: 30px; color: #333;">About Our Services</h2>
                        <p style="font-size: 16px; line-height: 1.8; color: #666; margin-bottom: 20px;">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.</p>
                        <p style="font-size: 16px; line-height: 1.8; color: #666; margin-bottom: 20px;">Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</p>
                        <p style="font-size: 16px; line-height: 1.8; color: #666;">Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis.</p>
                      </div>
                    </div>
                  </div>
                </section>
              `,
              media: '<i class="fa fa-paragraph"></i>'
            }
          ]
        },
        layerManager: {
          appendTo: '.layers-container'
        },
        styleManager: {
          appendTo: '.styles-container',
          sectors: [
            {
              name: 'General',
              open: false,
              buildProps: ['display', 'position', 'float'],
              properties: [
                {
                  name: 'Display',
                  property: 'display'
                },
                {
                  name: 'Position',
                  property: 'position'
                }
              ]
            },
            {
              name: 'Dimension',
              open: false,
              buildProps: ['width', 'height', 'padding', 'margin'],
              properties: [
                {
                  name: 'Width',
                  property: 'width'
                },
                {
                  name: 'Height',
                  property: 'height'
                }
              ]
            },
            {
              name: 'Typography',
              open: false,
              buildProps: ['font-family', 'font-size', 'font-weight', 'color', 'text-align'],
              properties: [
                {
                  name: 'Font Size',
                  property: 'font-size'
                },
                {
                  name: 'Color',
                  property: 'color'
                }
              ]
            },
            {
              name: 'Background',
              open: false,
              buildProps: ['background-color', 'background-image'],
              properties: [
                {
                  name: 'Background Color',
                  property: 'background-color'
                }
              ]
            }
          ]
        },
        traitManager: {
          appendTo: '.traits-container'
        },
        panels: {
          defaults: [
            {
              id: 'panel-switcher',
              el: '.panel__switcher',
              buttons: [
                {
                  id: 'show-layers',
                  active: true,
                  label: 'Layers',
                  command: 'show-layers',
                  togglable: false,
                },
                {
                  id: 'show-style',
                  active: true,
                  label: 'Styles',
                  command: 'show-styles',
                  togglable: false,
                },
                {
                  id: 'show-traits',
                  active: true,
                  label: 'Settings',
                  command: 'show-traits',
                  togglable: false,
                }
              ],
            }
          ]
        },
        deviceManager: {
          devices: [
            {
              name: 'Desktop',
              width: '',
            },
            {
              name: 'Mobile',
              width: '320px',
              widthMedia: '480px',
            }
          ]
        }
      });

      editorInstance.Commands.add('show-layers', {
        run(editor) {
          document.querySelector('.layers-container').style.display = '';
          document.querySelector('.styles-container').style.display = 'none';
          document.querySelector('.traits-container').style.display = 'none';
        }
      });

      editorInstance.Commands.add('show-styles', {
        run(editor) {
          document.querySelector('.layers-container').style.display = 'none';
          document.querySelector('.styles-container').style.display = '';
          document.querySelector('.traits-container').style.display = 'none';
        }
      });

      editorInstance.Commands.add('show-traits', {
        run(editor) {
          document.querySelector('.layers-container').style.display = 'none';
          document.querySelector('.styles-container').style.display = 'none';
          document.querySelector('.traits-container').style.display = '';
        }
      });

      setEditor(editorInstance);
    } catch (error) {
      console.error('Failed to load GrapesJS:', error);
      message.error('Failed to load page builder');
    }
  };

  const handleSave = async () => {
    if (!editor) {
      message.error('Editor not initialized');
      return;
    }

    try {
      const values = await form.validateFields();
      console.log('Form values:', values);
      setLoading(true);

      const htmlContent = editor.getHtml();
      const cssContent = editor.getCss();
      console.log('HTML Content:', htmlContent);
      console.log('CSS Content:', cssContent);

      const payload = {
        title: values.title,
        slug: values.slug,
        metaTitle: values.metaTitle || values.title,
        metaDescription: values.metaDescription || '',
        keywords: values.keywords || '',
        isActive: values.isActive || false,
        htmlContent,
        cssContent,
        sections: []
      };
      
      console.log('Payload being sent:', payload);

      let response;
      if (editingPage?.id) {
        console.log('Updating page with ID:', editingPage.id);
        response = await ApiClient.put(`admin/landing-pages/${editingPage.id}`, {}, payload);
      } else {
        console.log('Creating new page');
        response = await ApiClient.post('admin/landing-pages', payload);
      }
      
      console.log('API Response:', response);
      
      if (response && (response.status === 200 || response.status === 201)) {
        message.success('Page saved successfully!');
        if (onSave) onSave(payload);
      } else {
        console.error('Unexpected response:', response);
        message.error('Save failed - unexpected response');
      }
    } catch (error) {
      console.error('Save error details:', error);
      console.error('Error response:', error.response);
      message.error(`Failed to save page: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    if (!editor) return;
    
    const html = editor.getHtml();
    const css = editor.getCss();
    const previewContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <link href="https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/css/bootstrap.min.css" rel="stylesheet">
          <style>${css}</style>
        </head>
        <body>
          ${html}
          <script>
            function handleFormSubmit(event) {
              event.preventDefault();
              const formData = new FormData(event.target);
              const data = {
                Name: formData.get('Name'),
                Email: formData.get('Email'),
                Phone: formData.get('Phone'),
                LastName: document.title || 'Landing Page'
              };
              
              fetch('${process.env.REACT_APP_BASE_URL}StoreVisitors/visitorDetails', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
              })
              .then(response => response.json())
              .then(result => {
                if (result.status === 201) {
                  alert('Enquiry submitted successfully!');
                  event.target.reset();
                } else {
                  alert('Failed to submit enquiry. Please try again.');
                }
              })
              .catch(error => {
                console.error('Error:', error);
                alert('Failed to submit enquiry. Please try again.');
              });
            }
          </script>
        </body>
      </html>
    `;
    
    const blob = new Blob([previewContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  return (
    <Modal
      title="Visual Page Builder"
      visible={visible}
      onCancel={() => {
        if (editor) {
          try {
            editor.destroy();
          } catch (e) {
            console.log('Editor cleanup on close:', e);
          }
          setEditor(null);
        }
        onClose();
      }}
      footer={null}
      width="98vw"
      style={{ top: 10 }}
      bodyStyle={{ height: '90vh', padding: 0 }}
      destroyOnClose
      maskClosable={false}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ 
          padding: '10px 20px', 
          borderBottom: '1px solid #e8e8e8', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#fafafa'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flex: 1 }}>
            <Form form={form} layout="inline" style={{ flex: 1 }}>
              <Form.Item name="title" rules={[{ required: true }]}>
                <Input placeholder="Page Title" style={{ width: 150 }} onChange={handleTitleChange} />
              </Form.Item>
              <Form.Item name="slug" rules={[{ required: true }]}>
                <Input placeholder="page-slug" addonBefore="/" style={{ width: 150 }} />
              </Form.Item>
              <Form.Item name="metaTitle">
                <Input placeholder="SEO Title" style={{ width: 150 }} />
              </Form.Item>
              <Form.Item name="metaDescription">
                <Input.TextArea placeholder="SEO Description" style={{ width: 200, minHeight: 32 }} autoSize={{ minRows: 1, maxRows: 2 }} />
              </Form.Item>
              <Form.Item name="keywords">
                <Input placeholder="Keywords (comma separated)" style={{ width: 150 }} />
              </Form.Item>
              <Form.Item name="isActive" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Draft" />
              </Form.Item>
            </Form>
          </div>
          <Space>
            <Button icon={<EyeOutlined />} onClick={handlePreview}>
              Preview
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSave}>
              Save Page
            </Button>
            <Button icon={<CloseOutlined />} onClick={() => {
              if (editor) {
                try {
                  editor.destroy();
                } catch (e) {
                  console.log('Editor cleanup on close button:', e);
                }
                setEditor(null);
              }
              onClose();
            }}>
              Close
            </Button>
          </Space>
        </div>

        <div style={{ flex: 1, display: 'flex' }}>
          <div style={{ width: '250px', borderRight: '1px solid #e8e8e8', backgroundColor: '#f8f9fa' }}>
            <div style={{ padding: '15px', borderBottom: '1px solid #e8e8e8' }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Blocks</h4>
            </div>
            <div className="blocks-container" style={{ padding: '10px' }}></div>
          </div>

          <div style={{ flex: 1 }} ref={editorRef}></div>

          <div style={{ width: '250px', borderLeft: '1px solid #e8e8e8', backgroundColor: '#f8f9fa' }}>
            <div className="panel__switcher" style={{ 
              display: 'flex', 
              borderBottom: '1px solid #e8e8e8',
              backgroundColor: '#fff'
            }}></div>
            <div className="layers-container" style={{ padding: '10px' }}></div>
            <div className="styles-container" style={{ padding: '10px', display: 'none' }}></div>
            <div className="traits-container" style={{ padding: '10px', display: 'none' }}></div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default GrapesJSBuilder;